import { Head, Link, router } from "@inertiajs/react"
import { Search } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"

import {
  mutateSubscription,
  summarizeSchedule,
} from "@/components/products/reports/SubscriptionsDialog"
import { viewSelectionUrl } from "@/components/products/reports/ViewPicker"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Pagination } from "@/components/ui/pagination"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import type {
  BreadcrumbItem,
  ReportPagination,
  ReportSubscriptionData,
} from "@/types"

interface ReportOption {
  key: string
  title: string
}

interface Props {
  subscriptions: ReportSubscriptionData[]
  pagination: ReportPagination
  reports?: ReportOption[]
  filters?: {
    q?: string
    report_key?: string
    status?: "enabled" | "disabled" | "needs_review"
  }
  capabilities: {
    manage_all: boolean
    add_recipient: boolean
  }
}

function recipientStatus(subscription: ReportSubscriptionData) {
  if (subscription.review_required) return "Needs review"
  return subscription.enabled ? "Active" : "Unsubscribed"
}

export default function ReportSubscriptions({
  subscriptions,
  pagination,
  reports = [],
  filters = {},
  capabilities,
}: Props) {
  const routes = useOrgRoutes()
  const indexPath = routes.reportsPath()
  const pagePath = routes.reportSubscriptionsOverviewPath()
  const [query, setQuery] = useState(filters.q ?? "")
  const [reportKey, setReportKey] = useState(filters.report_key ?? "all")
  const [status, setStatus] = useState(filters.status ?? "all")
  const [loading, setLoading] = useState(false)
  const [resolvingId, setResolvingId] = useState<number | null>(null)
  const paginationLimit = pagination.limit ?? pagination.per_page ?? 25
  const paginationCount = pagination.count ?? pagination.total ?? 0
  const paginationMetadata = {
    count: paginationCount,
    page: pagination.page,
    limit: paginationLimit,
    pages: pagination.pages,
    previous:
      pagination.previous ?? (pagination.page > 1 ? pagination.page - 1 : null),
    next:
      pagination.next ??
      (pagination.page < pagination.pages ? pagination.page + 1 : null),
  }

  const applyFilters = (event: React.FormEvent) => {
    event.preventDefault()
    setLoading(true)
    router.get(
      pagePath,
      {
        ...(query.trim() ? { q: query.trim() } : {}),
        ...(reportKey !== "all" ? { report_key: reportKey } : {}),
        ...(status !== "all" ? { status } : {}),
      },
      {
        preserveState: true,
        preserveScroll: true,
        replace: true,
        onFinish: () => setLoading(false),
      },
    )
  }

  const resolveUnavailableSubscription = async (
    subscription: ReportSubscriptionData,
  ) => {
    const remove = Boolean(
      subscription.capabilities?.remove &&
      !subscription.capabilities?.unsubscribe,
    )
    const verb = remove ? "Remove" : "Unsubscribe from"
    if (!window.confirm(`${verb} this unavailable organization view?`)) return

    setResolvingId(subscription.id)
    try {
      await mutateSubscription(
        routes.reportSubscriptionPath(subscription.id),
        remove ? "DELETE" : "PATCH",
        remove ? undefined : { enabled: false },
      )
      toast.success(remove ? "Subscription removed." : "Unsubscribed.")
      router.reload({ only: ["subscriptions", "pagination"] })
    } catch (reason) {
      toast.error(
        reason instanceof Error
          ? reason.message
          : "Could not update this subscription.",
      )
    } finally {
      setResolvingId(null)
    }
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Reports", href: indexPath },
    { title: "Subscriptions", href: "" },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Report subscriptions" />
      <div className="space-y-6 p-4 sm:p-6">
        <div className="space-y-4">
          <div>
            <h1 className="text-2xl font-semibold">Report subscriptions</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              {capabilities.manage_all
                ? "Review recurring deliveries across the organization. Add new subscribers from the report view they should receive."
                : "Review your recurring report deliveries. Open a report view to manage your subscription."}
            </p>
          </div>
          <nav className="flex gap-1 border-b" aria-label="Reports">
            <Link
              href={indexPath}
              className="text-muted-foreground hover:text-foreground flex h-11 items-center border-b-2 border-transparent px-3 text-sm font-medium"
            >
              All reports
            </Link>
            <Link
              href={pagePath}
              aria-current="page"
              className="border-primary text-foreground flex h-11 items-center border-b-2 px-3 text-sm font-medium"
            >
              Subscriptions
            </Link>
          </nav>
        </div>

        <form
          onSubmit={applyFilters}
          className="grid gap-3 rounded-lg border p-4 md:grid-cols-2 md:items-end xl:grid-cols-[minmax(14rem,1fr)_14rem_12rem_auto]"
          aria-label="Filter report subscriptions"
        >
          <div className="space-y-1.5">
            <Label htmlFor="subscription-search">Search</Label>
            <div className="relative">
              <Search className="text-muted-foreground pointer-events-none absolute top-3.5 left-3 size-4" />
              <Input
                id="subscription-search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Recipient, report, or view"
                className="h-11 pl-9"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="subscription-report">Report</Label>
            <Select value={reportKey} onValueChange={setReportKey}>
              <SelectTrigger id="subscription-report" className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All reports</SelectItem>
                {reports.map((report) => (
                  <SelectItem key={report.key} value={report.key}>
                    {report.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="subscription-status">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger id="subscription-status" className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="enabled">Active</SelectItem>
                <SelectItem value="disabled">Unsubscribed</SelectItem>
                <SelectItem value="needs_review">Needs review</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" className="h-11" disabled={loading}>
            {loading ? "Loading…" : "Apply"}
          </Button>
        </form>

        {subscriptions.length === 0 ? (
          <div className="rounded-lg border border-dashed px-5 py-12 text-center">
            <h2 className="font-medium">No subscriptions found</h2>
            <p className="text-muted-foreground mt-1 text-sm">
              {capabilities.add_recipient
                ? "Try different filters, or open a report view to add a subscriber."
                : "Try different filters, or open a report view to subscribe."}
            </p>
          </div>
        ) : (
          <>
            <div className="hidden overflow-hidden rounded-lg border md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Report</TableHead>
                    <TableHead>View</TableHead>
                    <TableHead>Delivery</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscriptions.map((subscription) => (
                    <TableRow key={subscription.id}>
                      <TableCell className="max-w-64 font-medium break-all whitespace-normal">
                        {subscription.recipient_label}
                      </TableCell>
                      <TableCell className="max-w-64 break-words whitespace-normal">
                        {subscription.report_title ?? subscription.report_key}
                      </TableCell>
                      <TableCell className="max-w-64 whitespace-normal">
                        <div className="flex flex-wrap items-center gap-2">
                          {subscription.view_accessible === false ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-muted-foreground break-words">
                                {subscription.view_name}
                              </span>
                              {(subscription.capabilities?.unsubscribe ||
                                subscription.capabilities?.remove) && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-11"
                                  disabled={resolvingId === subscription.id}
                                  onClick={() =>
                                    void resolveUnavailableSubscription(
                                      subscription,
                                    )
                                  }
                                >
                                  {subscription.capabilities?.remove
                                    ? "Remove"
                                    : "Unsubscribe"}
                                </Button>
                              )}
                            </div>
                          ) : (
                            <Link
                              href={viewSelectionUrl(
                                routes.reportPath(subscription.report_key),
                                subscription.report_view_id,
                              )}
                              className="text-primary inline-flex min-h-11 min-w-0 items-center break-all hover:underline"
                              title={subscription.view_name || "Default"}
                            >
                              {subscription.view_name || "Default"}
                            </Link>
                          )}
                          {subscription.view_status === "needs_review" && (
                            <Badge variant="outline">Needs review</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-64 text-xs break-all whitespace-normal">
                        {summarizeSchedule(subscription)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            subscription.enabled ? "secondary" : "outline"
                          }
                        >
                          {recipientStatus(subscription)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="space-y-3 md:hidden">
              {subscriptions.map((subscription) => (
                <article
                  key={subscription.id}
                  className="space-y-3 rounded-lg border p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="text-sm font-medium break-all">
                        {subscription.recipient_label}
                      </h2>
                      <p className="text-muted-foreground text-xs">
                        {subscription.report_title ?? subscription.report_key}
                      </p>
                    </div>
                    <Badge
                      variant={subscription.enabled ? "secondary" : "outline"}
                    >
                      {recipientStatus(subscription)}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">View</p>
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      {subscription.view_accessible === false ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-muted-foreground text-sm break-words">
                            {subscription.view_name}
                          </span>
                          {(subscription.capabilities?.unsubscribe ||
                            subscription.capabilities?.remove) && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-11"
                              disabled={resolvingId === subscription.id}
                              onClick={() =>
                                void resolveUnavailableSubscription(
                                  subscription,
                                )
                              }
                            >
                              {subscription.capabilities?.remove
                                ? "Remove"
                                : "Unsubscribe"}
                            </Button>
                          )}
                        </div>
                      ) : (
                        <Link
                          href={viewSelectionUrl(
                            routes.reportPath(subscription.report_key),
                            subscription.report_view_id,
                          )}
                          className="text-primary inline-flex min-h-11 min-w-0 items-center text-sm font-medium break-all"
                          title={subscription.view_name || "Default"}
                        >
                          {subscription.view_name || "Default"}
                        </Link>
                      )}
                      {subscription.view_status === "needs_review" && (
                        <Badge variant="outline">Needs review</Badge>
                      )}
                    </div>
                  </div>
                  <p className="text-muted-foreground text-xs break-all">
                    {summarizeSchedule(subscription)}
                  </p>
                </article>
              ))}
            </div>
          </>
        )}

        <Pagination
          pagination={paginationMetadata}
          baseUrl={pagePath}
          className="[&_a]:min-h-11 [&_a]:min-w-11 [&_button]:min-h-11 [&_button]:min-w-11"
        />
      </div>
    </AppLayout>
  )
}
